<template>
    <div>
        <!-- Sidebar card -->
        <card
            class="mb-3 flex flex-col dark:text-white"
            v-if="audit && showCard"
        >
            <!-- Header -->
            <div class="mb-4 flex items-center justify-between">
                <span class="font-mono text-xs text-gray-400 dark:text-gray-500">{{ audit.hash }}</span>
                <audit-status :audit="audit" />
            </div>

            <!-- Locks -->
            <div class="space-y-2">
                <div
                    class="flex items-center justify-between rounded-lg border px-3 py-2.5"
                    :class="audit.block_products
                        ? 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/5'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/30'"
                >
                    <div class="flex items-center gap-2.5">
                        <div
                            class="h-2 w-2 rounded-full"
                            :class="audit.block_products ? 'bg-red-500' : 'bg-emerald-500'"
                        ></div>
                        <span class="text-sm text-gray-700 dark:text-gray-300">Produtos</span>
                    </div>
                    <button
                        @click.prevent="toggleProducts"
                        :disabled="loading || audit.status === 'closed'"
                        class="rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-30"
                        :class="audit.block_products
                            ? 'text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-500/10'
                            : 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-500/10'"
                    >
                        {{ audit.block_products ? "Liberar" : "Bloquear" }}
                    </button>
                </div>

                <div
                    class="flex items-center justify-between rounded-lg border px-3 py-2.5"
                    :class="audit.block_cashout
                        ? 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/5'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/30'"
                >
                    <div class="flex items-center gap-2.5">
                        <div
                            class="h-2 w-2 rounded-full"
                            :class="audit.block_cashout ? 'bg-red-500' : 'bg-emerald-500'"
                        ></div>
                        <span class="text-sm text-gray-700 dark:text-gray-300">Saldo</span>
                    </div>
                    <button
                        @click.prevent="toggleBalance"
                        :disabled="loading || audit.status === 'closed'"
                        class="rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-30"
                        :class="audit.block_cashout
                            ? 'text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-500/10'
                            : 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-500/10'"
                    >
                        {{ audit.block_cashout ? "Liberar" : "Bloquear" }}
                    </button>
                </div>
            </div>

            <!-- Stage -->
            <div class="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                <div class="mb-2">
                    <span class="text-xs text-gray-500">Etapa</span>
                </div>
                <div class="flex items-center gap-2">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                        :class="stagePillClass(audit.stage)"
                    >
                        <span class="h-1.5 w-1.5 rounded-full" :class="stageDotClass(audit.stage)"></span>
                        {{ stageName(audit.stage) }}
                    </span>

                    <select
                        v-if="audit.status !== 'closed'"
                        v-model="selectedStage"
                        @change="onStageSelected"
                        :disabled="loading"
                        class="ml-auto appearance-none rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 transition-colors hover:border-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-0 disabled:opacity-30 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:border-gray-500"
                    >
                        <option value="">Alterar...</option>
                        <option
                            v-for="stage in availableStages"
                            :key="stage.value"
                            :value="stage.value"
                            :disabled="stage.value === audit.stage"
                        >
                            {{ stage.label }}
                        </option>
                    </select>
                </div>
            </div>
        </card>

        <!-- Timeline -->
        <card v-if="showInteractions" class="mb-4">
            <timeline-skeleton v-if="loading" />
            <ol
                v-else
                class="relative w-full border-l border-gray-300 dark:border-gray-600"
            >
                <li
                    class="mb-8 ml-5"
                    v-for="interaction in audit.interactions"
                >
                    <dice-bear-avatar
                        v-if="interaction.user"
                        :seed="interaction.user.name"
                        class="absolute -left-3 h-6 w-6 rounded-full ring-2 ring-white dark:ring-gray-800"
                    />
                    <div
                        v-else
                        class="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white bg-gray-400 dark:border-gray-800 dark:bg-gray-600"
                    ></div>

                    <div class="flex items-start justify-between">
                        <div>
                            <p
                                v-if="interaction.user"
                                class="text-xs font-medium text-gray-700 dark:text-gray-300"
                                v-tooltip="interaction.user.email"
                            >
                                {{ interaction.user.name }}
                            </p>
                            <p
                                class="text-xs text-gray-500"
                                v-if="interaction.interaction_type !== 'comment'"
                            >
                                {{ interactionName(interaction.interaction_type) }}
                            </p>
                        </div>
                        <time class="text-right font-mono text-[10px] text-gray-400 dark:text-gray-500">
                            {{ interaction.timestamp }}
                        </time>
                    </div>
                    <div
                        v-if="interaction.description"
                        class="mt-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-700/50 dark:text-gray-400"
                    >
                        <span v-if="interaction.interaction_type === 'notification'" class="text-indigo-600 dark:text-indigo-400">
                            {{ notificationName(interaction.description) }}
                        </span>
                        <span v-else>{{ interaction.description }}</span>
                    </div>
                </li>

                <!-- Origin marker -->
                <li class="ml-5">
                    <div
                        class="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white bg-indigo-500 dark:border-gray-800"
                    ></div>
                    <div class="flex items-start justify-between">
                        <div>
                            <p class="text-sm font-medium text-gray-900 dark:text-white">Auditoria Iniciada</p>
                            <p
                                v-if="audit.investigator"
                                class="mt-0.5 text-xs text-gray-500"
                            >
                                {{ audit.investigator.name }}
                            </p>
                        </div>
                        <time class="font-mono text-[10px] text-gray-400 dark:text-gray-500">
                            {{ audit.created_at }}
                        </time>
                    </div>
                    <div class="mt-2 flex gap-3">
                        <span class="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <span
                                class="h-1.5 w-1.5 rounded-full"
                                :class="audit.block_products ? 'bg-red-500' : 'bg-emerald-500'"
                            ></span>
                            Produtos
                        </span>
                        <span class="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <span
                                class="h-1.5 w-1.5 rounded-full"
                                :class="audit.block_cashout ? 'bg-red-500' : 'bg-emerald-500'"
                            ></span>
                            Saque
                        </span>
                    </div>
                </li>
            </ol>
        </card>

        <!-- Comment form -->
        <div v-if="showInteractions">
            <card>
                <form @submit.prevent="comment">
                    <textarea
                        v-model="form.description"
                        id="comment"
                        rows="2"
                        class="w-full resize-none border-0 bg-transparent px-0 py-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500"
                        placeholder="Escreva um comentário..."
                        required
                    ></textarea>

                    <div class="flex items-center justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
                        <button
                            v-if="audit.status !== 'closed'"
                            @click.prevent="showFinishModal = true"
                            type="button"
                            class="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                        >
                            {{ form.description.length > 0 ? "Comentar e " : "" }}Encerrar
                        </button>
                        <button
                            type="submit"
                            class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-500"
                        >
                            Comentar
                        </button>
                    </div>
                </form>
            </card>
        </div>

        <!-- Stage change modal -->
        <modal :open="showStageModal" @close="cancelStageChange">
            <div class="p-1">
                <div class="mb-1 text-sm font-medium text-gray-900 dark:text-white">Alterar Etapa</div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    Alterar para <strong class="text-gray-900 dark:text-white">{{ stageName(selectedStage) }}</strong>
                </p>
                <div class="mt-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/20 dark:bg-amber-500/5">
                    <ExclamationTriangleIcon class="h-4 w-4 flex-shrink-0 text-amber-500" />
                    <p class="text-xs text-amber-700 dark:text-amber-400">
                        Um e-mail será enviado automaticamente ao usuário.
                    </p>
                </div>
                <div class="mt-4 flex justify-end gap-2">
                    <button
                        @click="cancelStageChange"
                        class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancelar
                    </button>
                    <button
                        @click="confirmStageChange"
                        class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-500"
                    >
                        Confirmar
                    </button>
                </div>
            </div>
        </modal>

        <!-- Close audit modal -->
        <modal :open="showFinishModal" @close="showFinishModal = false">
            <div class="p-1">
                <div class="mb-1 text-sm font-medium text-gray-900 dark:text-white">Encerrar Auditoria</div>
                <p class="mb-3 text-sm text-gray-500 dark:text-gray-400">
                    As seguintes ações serão executadas:
                </p>
                <div class="space-y-1.5">
                    <div v-if="audit.block_products" class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        Produtos serão desbloqueados
                    </div>
                    <div v-if="audit.block_cashout" class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        Saldo será liberado
                    </div>
                    <div class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                        Auditoria será encerrada
                    </div>
                </div>
                <div class="mt-4 flex justify-end gap-2">
                    <button
                        @click="showFinishModal = false"
                        class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancelar
                    </button>
                    <button
                        @click="confirmFinish"
                        class="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-500"
                    >
                        Encerrar
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import TimelineSkeleton from "../layouts/skeleton/TimelineSkeleton.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";
import BooleanIcon from "../layouts/BooleanIcon.vue";
import Card from "../layouts/Card.vue";
import Modal from "../layouts/Modal.vue";
import AuditStatus from "./AuditStatus.vue";
import { CheckCircleIcon, NoSymbolIcon, ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import SubmitButton from "../forms/SubmitButton.vue";
import { notify } from "notiwind";
import DiceBearAvatar from "../common/DiceBearAvatar.vue";

export default {
    components: {
        DiceBearAvatar,
        SubmitButton,
        NoSymbolIcon,
        CheckCircleIcon,
        ExclamationTriangleIcon,
        AuditStatus,
        Card,
        BooleanIcon,
        TimelineSkeleton,
        Modal,
    },
    props: {
        audit: { default: () => {} },
        showCard: { default: () => false },
        showInteractions: { default: () => true },
    },
    mixins: [HelperMixin],
    emits: ["fetch"],
    data() {
        return {
            loading: false,
            approve: false,
            showFinishModal: false,
            showStageModal: false,
            selectedStage: "",
            availableStages: [
                { value: "under_review", label: "Em Análise" },
                { value: "under_appeal", label: "Em Contestação" },
                { value: "approved", label: "Aprovado" },
                { value: "temporary_block", label: "Bloqueio Temporário" },
                { value: "permanent_block", label: "Bloqueio Permanente" },
            ],
            form: {
                status: "open",
                description: "",
                type: "comment",
                stage: null,
            },
        };
    },

    computed: {
        sortedInteractions() {
            if (!this.audit || !this.audit.interactions) {
                return [];
            }

            return this.audit.interactions;
        },
    },

    methods: {
        notify,
        stageName(stage) {
            const map = {
                under_review: "Em Análise",
                under_appeal: "Em Contestação",
                approved: "Aprovado",
                temporary_block: "Bloqueio Temporário",
                permanent_block: "Bloqueio Permanente",
            };

            return map[stage] || stage;
        },
        stagePillClass(stage) {
            const map = {
                under_review: "bg-amber-100 text-amber-700 ring-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20",
                under_appeal: "bg-orange-100 text-orange-700 ring-orange-300 dark:bg-orange-500/10 dark:text-orange-400 dark:ring-orange-500/20",
                approved: "bg-emerald-100 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20",
                temporary_block: "bg-red-100 text-red-700 ring-red-300 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20",
                permanent_block: "bg-red-100 text-red-800 ring-red-400 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30",
            };

            return map[stage] || "bg-gray-100 text-gray-600 ring-gray-300 dark:bg-gray-600/30 dark:text-gray-400 dark:ring-gray-600";
        },
        stageDotClass(stage) {
            const map = {
                under_review: "bg-amber-500 dark:bg-amber-400",
                under_appeal: "bg-orange-500 dark:bg-orange-400",
                approved: "bg-emerald-500 dark:bg-emerald-400",
                temporary_block: "bg-red-500 dark:bg-red-400",
                permanent_block: "bg-red-600 dark:bg-red-300",
            };

            return map[stage] || "bg-gray-400 dark:bg-gray-500";
        },
        onStageSelected() {
            if (this.selectedStage && this.selectedStage !== this.audit.stage) {
                this.showStageModal = true;
            }
        },
        cancelStageChange() {
            this.selectedStage = "";
            this.showStageModal = false;
        },
        confirmStageChange() {
            this.showStageModal = false;
            this.form.type = "stage_change";
            this.form.stage = this.selectedStage;
            this.form.description = `Etapa alterada para ${this.stageName(this.selectedStage)}`;
            this.submit().then(() => {
                this.form.description = "";
                this.form.stage = null;
                this.form.type = "comment";
                this.selectedStage = "";
            });
        },
        confirmFinish() {
            this.finishAudit();
            this.showFinishModal = false;
        },
        finishAudit() {
            this.form.type = "closed";
            this.form.status = "closed";
            this.submit().then(() => {
                this.form.description = "";
            });
        },
        toggleProducts() {
            this.form.type = this.audit.block_products
                ? "unblockProducts"
                : "blockProducts";
            this.submit().then(() => {
                this.form.description = "";
            });
        },
        toggleBalance() {
            this.form.type = this.audit.block_cashout
                ? "unblockCashout"
                : "blockCashout";
            this.submit().then(() => {
                this.form.description = "";
            });
        },
        comment() {
            this.form.type = "comment";
            this.submit().then(() => {
                this.form.description = "";
            });
        },
        submit() {
            this.loading = true;
            return axios
                .post("/api/audit/" + this.audit.id + "/interaction", this.form)
                .then(() => {
                    this.$emit("fetch");
                    this.loading = false;
                });
        },
        notificationName(name) {
            const map = {
                "App\\Notifications\\User\\Audit\\RefundNeeded": "Reembolso Requerido",
                "App\\Notifications\\User\\Audit\\AccountBlocked": "Conta Bloqueada",
                "App\\Notifications\\User\\Audit\\UnderReviewNotification": "Conta em Análise",
                "App\\Notifications\\User\\Audit\\UnderAppealNotification": "Contestação Recebida",
                "App\\Notifications\\User\\Audit\\ApprovedNotification": "Conta Liberada",
                "App\\Notifications\\User\\Audit\\TemporaryBlockNotification": "Bloqueio Temporário",
                "App\\Notifications\\User\\Audit\\PermanentBlockNotification": "Bloqueio Permanente",
            };

            return map[name] || name;
        },
        interactionName(name) {
            const map = {
                blockProducts: "Produtos Bloqueados",
                unblockProducts: "Produtos Desbloqueados",
                blockCashout: "Saldo Bloqueado",
                unblockCashout: "Saldo Desbloqueado",
                comment: "Comentário",
                closed: "Finalizado",
                notification: "Notificação",
                stage_change: "Etapa Alterada",
            };

            return map[name] || name;
        },
        interactionClass(name) {
            const map = {
                comment: "bg-blue-400 text-white",
                closed: "bg-red-400 text-white",
                stage_change: "bg-indigo-400 text-white",
            };

            return map[name] || name;
        },
    },
};
</script>
